/**
 * UNIFIED Pricing Service - Single source of truth
 */
const CENTRAL_API_URL = process.env.REACT_APP_CENTRAL_API_URL || 'https://us-central1-paymentgateway-ab783.cloudfunctions.net/api';
const APP_ID = process.env.REACT_APP_APP_ID || 'booking-system';

class PricingService {
  constructor() {
    this.cache = null;
    this.cacheTime = 0;
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes — pricing rarely changes, avoid redundant fetches
    this.listeners = new Set();
  }

  async fetchPricing(forceRefresh = false) {
    const now = Date.now();
    if (!forceRefresh && this.cache && (now - this.cacheTime) < this.CACHE_DURATION) {
      console.log('✅ Using cached pricing (age: ' + Math.round((now - this.cacheTime) / 1000) + 's)');
      return this.cache;
    }
    // Add cache-busting timestamp to ensure fresh data
    const endpoint = CENTRAL_API_URL + '/pricing/app/' + APP_ID + '?t=' + now;
    console.log('🔄 Fetching fresh pricing from:', endpoint);
    const response = await fetch(endpoint, {
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
    });
    if (!response.ok) throw new Error('HTTP ' + response.status);
    const data = await response.json();
    if (!data.success || !data.app) throw new Error('Invalid response');
    this.cache = data.app;
    this.cacheTime = now;
    console.log('✅ Fresh pricing loaded. Sample: Pro CAD = $' + (data.app.plans?.pro?.prices?.monthly_cad?.amount || 'N/A'));
    this.notifyListeners(this.cache);
    return this.cache;
  }

  async getFormattedForLanding() {
    const data = await this.fetchPricing();
    if (!data || !data.plans) return { plans: [], raw: null };
    const formattedPlans = ['basic', 'professional', 'business'].map(key => {
      const plan = data.plans[key];
      if (!plan) return null;
      return {
        id: key,
        title: plan.name || key,
        description: plan.description || '',
        features: plan.features || [],
        popular: plan.popular || false,
        prices: {
          usd: {
            monthly: plan.prices?.monthly_usd?.amount || 0,
            yearly: plan.prices?.yearly_usd?.amount || 0,
            monthlyPriceId: plan.prices?.monthly_usd?.stripePriceId,
            yearlyPriceId: plan.prices?.yearly_usd?.stripePriceId
          },
          cad: {
            monthly: plan.prices?.monthly_cad?.amount || 0,
            yearly: plan.prices?.yearly_cad?.amount || 0,
            monthlyPriceId: plan.prices?.monthly_cad?.stripePriceId,
            yearlyPriceId: plan.prices?.yearly_cad?.stripePriceId
          }
        }
      };
    }).filter(p => p);
    return { plans: formattedPlans, raw: data };
  }

  async getFormattedByCurrency(currency = 'USD') {
    const data = await this.fetchPricing();
    if (!data || !data.plans) throw new Error('No pricing data');
    const formatted = { currency, plans: {}, priceIds: {} };
    Object.entries(data.plans).filter(([, plan]) => !plan.isCustom && !plan.contactSales).forEach(([key, plan]) => {
      formatted.plans[key] = {
        name: plan.name,
        displayName: plan.displayName || plan.name,
        description: plan.description,
        features: plan.features || [],
        monthly: 0,
        yearly: 0,
        priceIds: { monthly: null, yearly: null },
        popular: plan.popular || false
      };
      Object.entries(plan.prices || {}).forEach(([priceKey, priceData]) => {
        if (priceData.currency.toUpperCase() === currency.toUpperCase()) {
          const interval = priceData.interval || (priceKey.includes('monthly') ? 'monthly' : 'yearly');
          if (interval) {
            formatted.plans[key][interval] = priceData.amount;
            formatted.plans[key].priceIds[interval] = priceData.stripePriceId;
          }
        }
      });
    });
    return formatted;
  }

  addListener(cb) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  notifyListeners(data) {
    this.listeners.forEach(cb => { try { cb(data); } catch(e) {} });
  }

  clearCache() {
    this.cache = null;
    this.cacheTime = 0;
  }

  async refresh() {
    return this.fetchPricing(true);
  }

  // Aliases for backward compatibility
  async getFormattedPricing(currency) { return this.getFormattedByCurrency(currency); }
  async getPricing() { return this.fetchPricing(); }
}

const pricingService = new PricingService();
export default pricingService;
